<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { useCategoriaExperienciaStore } from './store/useCategoriaExperienciaStore'
import type { ICategoriaExperiencia } from './types'
import LayoutTable from '@/components/CDF/LayoutTable.vue'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import { getI18n } from '@/plugins/i18n'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

const i18n = getI18n()
const { t } = useI18n(i18n)

definePage({
  meta: {
    action: 'list',
    subject: 'categoriaexperiencia',
  },
})

const store = useCategoriaExperienciaStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Icone',
      key: 'icon',
      sortable: false,
    },
    {
      title: 'Nome',
      key: 'descricao',
    },
  ] as IHeader[]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Icone',
      key: 'icon',
      sortable: false,
    },
    {
      title: 'Nome',
      value: 'descricao',
    },
  ] as ITerm[]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'categoriaexperiencia',
    },
    to: (item: ICategoriaExperiencia) => `/admin/categoria-experiencia/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'categoriaexperiencia',
    },
    onClick: (item: ICategoriaExperiencia) => dialogDestroy(item.id),
  },
]

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Categoria Experiências"

    new-item="/admin/categoria-experiencia/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.icon="{ item }">
      <VIcon
        color="primary"
        size="30"
        :icon="item?.icon_formatted"
      />
    </template>
  </LayoutTable>
</template>
